import { useMemo } from 'react';
import { motion } from 'framer-motion';

const EASE = [0.22, 0.61, 0.36, 1] as const;
const DRAMATIC_EASE = [0.16, 1, 0.3, 1] as const;

/* ────────────────────────────────────────────────────────────
   CINEMATIC BURST v2 — Premium particle system with:
   • Primary explosion wave (fast, dramatic)
   • Secondary bloom wave (slower, fills the scene)
   • Trailing sparkle particles
   • Pulsing origin glow with multiple rings
   • Ambient rising particles with longer life
   All GPU-composited via CSS transforms for 60fps.
   ──────────────────────────────────────────────────────────── */

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  angle: number;
  distance: number;
  delay: number;
  duration: number;
  color: string;
  blur: number;
  glow: string;
  shape: 'circle' | 'petal' | 'star' | 'sparkle';
  rotation: number;
  wave: 'primary' | 'secondary' | 'trail';
}

interface CinematicBurstProps {
  count?: number;
  colors: string[];
  shape?: 'circle' | 'petal' | 'star' | 'sparkle';
  originX?: number;
  originY?: number;
  spread?: number;
  active: boolean;
  ambientCount?: number;
  glowIntensity?: number;
  className?: string;
}

function generateParticles(
  count: number,
  colors: string[],
  shape: 'circle' | 'petal' | 'star' | 'sparkle',
  originX: number,
  originY: number,
  spread: number,
  glowIntensity: number
): Particle[] {
  const primary: Particle[] = [];
  const secondary: Particle[] = [];
  const trails: Particle[] = [];

  // Primary burst — fast, dramatic, large particles
  const primaryCount = Math.floor(count * 0.5);
  for (let i = 0; i < primaryCount; i++) {
    const angle = (i / primaryCount) * 360 + (Math.random() - 0.5) * 30;
    const color = colors[i % colors.length];
    const size = 8 + Math.random() * 18;
    const distance = spread * 0.5 + Math.random() * spread * 0.5;
    primary.push({
      id: i,
      x: originX, y: originY, size, angle, distance,
      delay: Math.random() * 0.15,
      duration: 2.0 + Math.random() * 1.2,
      color, blur: Math.random() > 0.7 ? 1 + Math.random() * 2 : 0,
      glow: `0 0 ${size * glowIntensity}px ${size * glowIntensity * 0.6}px ${color}`,
      shape, rotation: Math.random() * 360, wave: 'primary',
    });
  }

  // Secondary bloom — slower, fills gaps, smaller
  const secondaryCount = Math.floor(count * 0.35);
  for (let i = 0; i < secondaryCount; i++) {
    const angle = (i / secondaryCount) * 360 + (Math.random() - 0.5) * 50;
    const color = colors[(i + 3) % colors.length];
    const size = 4 + Math.random() * 12;
    const distance = spread * 0.3 + Math.random() * spread * 0.7;
    secondary.push({
      id: i + 500,
      x: originX, y: originY, size, angle, distance,
      delay: 0.6 + Math.random() * 0.4,
      duration: 2.5 + Math.random() * 1.5,
      color, blur: Math.random() > 0.5 ? 1 + Math.random() * 3 : 0,
      glow: `0 0 ${size * glowIntensity * 0.8}px ${size * glowIntensity * 0.4}px ${color}`,
      shape, rotation: Math.random() * 360, wave: 'secondary',
    });
  }

  // Trailing sparkles — tiny, long-lived, drift slowly
  const trailCount = Math.floor(count * 0.3);
  for (let i = 0; i < trailCount; i++) {
    const angle = Math.random() * 360;
    const color = colors[(i + 7) % colors.length];
    const size = 2 + Math.random() * 6;
    const distance = spread * 0.2 + Math.random() * spread * 0.4;
    trails.push({
      id: i + 1000,
      x: originX + (Math.random() - 0.5) * 10,
      y: originY + (Math.random() - 0.5) * 10,
      size, angle, distance,
      delay: 1.2 + Math.random() * 1.0,
      duration: 3.0 + Math.random() * 2.0,
      color, blur: 1 + Math.random() * 2,
      glow: `0 0 ${size * glowIntensity * 1.2}px ${size * glowIntensity * 0.8}px ${color}`,
      shape: 'sparkle', rotation: Math.random() * 360, wave: 'trail',
    });
  }

  return [...primary, ...secondary, ...trails];
}

function PetalShape({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size * 1.4} viewBox="0 0 20 28">
      <ellipse cx="10" cy="14" rx="8" ry="13" fill={color} />
      <ellipse cx="10" cy="12" rx="5" ry="9" fill="white" opacity="0.25" />
    </svg>
  );
}

function StarShape({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      <polygon
        points="12,0 15.1,8.5 24,9.3 17.3,15.2 19.4,24 12,19.5 4.6,24 6.7,15.2 0,9.3 8.9,8.5"
        fill={color}
      />
      <polygon
        points="12,4 14.2,10 20,10.5 15.6,14.4 17,20 12,17 7,20 8.4,14.4 4,10.5 9.8,10"
        fill="white"
        opacity="0.3"
      />
    </svg>
  );
}

function SparkleShape({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20">
      <path d="M10 0 L12 8 L20 10 L12 12 L10 20 L8 12 L0 10 L8 8 Z" fill={color} />
      <path d="M10 4 L11 8 L16 10 L11 12 L10 16 L9 12 L4 10 L9 8 Z" fill="white" opacity="0.35" />
    </svg>
  );
}

function ParticleElement({ particle }: { particle: Particle }) {
  const radians = (particle.angle * Math.PI) / 180;
  const endX = Math.cos(radians) * particle.distance;
  const endY = Math.sin(radians) * particle.distance;

  // Different motion profiles per wave
  const getKeyframes = () => {
    if (particle.wave === 'primary') {
      return {
        x: [0, endX * 0.3, endX * 0.8, endX],
        y: [0, endY * 0.3 - 20, endY * 0.8 - 10, endY + 30],
        scale: [0, 2.0, 1.2, 0],
        opacity: [0, 1, 0.8, 0],
        rotate: [0, particle.rotation * 0.5, particle.rotation, particle.rotation * 1.5],
      };
    }
    if (particle.wave === 'secondary') {
      return {
        x: [0, endX * 0.4, endX],
        y: [0, endY * 0.4 - 15, endY + 15],
        scale: [0, 1.5, 0],
        opacity: [0, 0.9, 0],
        rotate: [0, particle.rotation, particle.rotation * 2],
      };
    }
    // trail
    return {
      x: [0, endX * 0.5, endX],
      y: [0, endY * 0.5 - 8, endY],
      scale: [0, 1.0, 0.5, 0],
      opacity: [0, 0.6, 0.4, 0],
      rotate: [0, particle.rotation * 0.3],
    };
  };

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        filter: particle.blur > 0 ? `blur(${particle.blur}px)` : undefined,
        zIndex: 50,
      }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0, rotate: 0 }}
      animate={getKeyframes()}
      transition={{
        duration: particle.duration,
        delay: particle.delay,
        ease: particle.wave === 'primary' ? (DRAMATIC_EASE as any) : (EASE as any),
      }}
    >
      <div style={{ boxShadow: particle.glow, borderRadius: '50%' }}>
        {particle.shape === 'circle' && (
          <div
            style={{
              width: particle.size,
              height: particle.size,
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 35%, white, ${particle.color})`,
            }}
          />
        )}
        {particle.shape === 'petal' && <PetalShape color={particle.color} size={particle.size} />}
        {particle.shape === 'star' && <StarShape color={particle.color} size={particle.size} />}
        {particle.shape === 'sparkle' && <SparkleShape color={particle.color} size={particle.size} />}
      </div>
    </motion.div>
  );
}

export function CinematicBurst({
  count = 50,
  colors,
  shape = 'circle',
  originX = 50,
  originY = 50,
  spread = 50,
  active,
  ambientCount = 15,
  glowIntensity = 2.5,
  className = '',
}: CinematicBurstProps) {
  const particles = useMemo(
    () => (active ? generateParticles(count, colors, shape, originX, originY, spread, glowIntensity) : []),
    [active, count, colors, shape, originX, originY, spread, glowIntensity]
  );

  // Ambient drifting particles — longer lived, continuous
  const ambient = useMemo(
    () =>
      active
        ? Array.from({ length: ambientCount }, (_, i) => ({
            id: i + 2000,
            x: 5 + Math.random() * 90,
            size: 3 + Math.random() * 10,
            color: colors[i % colors.length],
            duration: 8 + Math.random() * 8,
            delay: 2.5 + Math.random() * 3,
            drift: (Math.random() - 0.5) * 80,
            shape,
            sway: 15 + Math.random() * 25,
          }))
        : [],
    [active, ambientCount, colors, shape]
  );

  if (!active) return null;

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} style={{ zIndex: 50 }}>
      {/* Pre-flash — brief intense white */}
      <motion.div
        className="absolute inset-0"
        style={{ background: 'white' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.15, 0] }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />

      {/* Central origin glow — pulsing */}
      <motion.div
        className="absolute"
        style={{
          left: `${originX}%`,
          top: `${originY}%`,
          width: 400,
          height: 400,
          marginLeft: -200,
          marginTop: -200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors[0]}55 0%, ${colors[1] || colors[0]}22 30%, transparent 65%)`,
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 3.5, 2, 2.5, 0], opacity: [0, 0.9, 0.4, 0.3, 0] }}
        transition={{ duration: 3.5, ease: 'easeOut' }}
      />

      {/* Primary burst ring */}
      <motion.div
        className="absolute"
        style={{
          left: `${originX}%`, top: `${originY}%`,
          width: 120, height: 120, marginLeft: -60, marginTop: -60,
          borderRadius: '50%',
          border: `2px solid ${colors[0]}88`,
          boxShadow: `0 0 40px 15px ${colors[0]}33`,
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 6], opacity: [1, 0] }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.05 }}
      />

      {/* Secondary burst ring — delayed */}
      <motion.div
        className="absolute"
        style={{
          left: `${originX}%`, top: `${originY}%`,
          width: 80, height: 80, marginLeft: -40, marginTop: -40,
          borderRadius: '50%',
          border: `1px solid ${colors[2] || colors[0]}66`,
          boxShadow: `0 0 30px 10px ${colors[2] || colors[0]}22`,
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 5], opacity: [0.7, 0] }}
        transition={{ duration: 1.3, ease: 'easeOut', delay: 0.5 }}
      />

      {/* Main burst particles */}
      {particles.map((p) => (
        <ParticleElement key={p.id} particle={p} />
      ))}

      {/* Ambient rising particles — with sway */}
      {ambient.map((a) => (
        <motion.div
          key={a.id}
          className="absolute pointer-events-none"
          style={{ left: `${a.x}%`, bottom: '-5%', zIndex: 45 }}
          animate={{
            y: [0, -(typeof window !== 'undefined' ? window.innerHeight * 1.4 : 1200)],
            x: [0, a.sway, -a.sway * 0.5, a.drift],
            rotate: [0, 180, 360],
            opacity: [0, 0.7, 0.6, 0.3, 0],
          }}
          transition={{
            duration: a.duration,
            delay: a.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          {a.shape === 'circle' && (
            <div style={{
              width: a.size, height: a.size, borderRadius: '50%',
              background: `radial-gradient(circle at 35% 35%, white, ${a.color})`,
              boxShadow: `0 0 ${a.size * 2}px ${a.size}px ${a.color}44`,
            }} />
          )}
          {a.shape === 'petal' && <PetalShape color={a.color} size={a.size} />}
          {a.shape === 'star' && <StarShape color={a.color} size={a.size} />}
          {a.shape === 'sparkle' && <SparkleShape color={a.color} size={a.size} />}
        </motion.div>
      ))}
    </div>
  );
}
